import { useCallback, useEffect, useRef } from 'react'

import type { Locale } from '@skytech/types'
import { DEFAULT_LOCALE } from '@skytech/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useIntl } from 'react-intl'

import { useAppContext } from '@/app/index.context'

import queries from '../queries'
import appResources from '../resources'

const MAX_ID_LENGTH = 50

const cropString = (string: string) => string.slice(0, MAX_ID_LENGTH).toLowerCase()

// Prepares translation
export const useMessage = (id = '') => {
  const { data, isSuccess, refetch } = useQuery(
    queries.translationList(),
    appResources.getTranslationList,
    { staleTime: Infinity }
  )

  const { locale } = useAppContext()
  const messages = data ? data[locale] || data[DEFAULT_LOCALE] : {}
  const croppedId = cropString(id)

  const postTranslationMutation = useMutation(
    () =>
      appResources.postTranslation({
        locale,
        code: croppedId,
        text: id,
        description: window.location.pathname,
      }),
    {
      onSettled: refetch,
    }
  )

  useEffect(() => {
    if (!messages[croppedId] && isSuccess && locale) {
      postTranslationMutation.mutate()
    }
  }, [])

  return croppedId
}

// Returns translation
export const useText = (id: string, values = {}) => {
  const croppedId = useMessage(id)
  const { formatMessage } = useIntl()

  return formatMessage({ id: croppedId }, values)
}

// Returns prepare-translation function
const usePrepareTranslation = () => {
  const createdTranslationIds = useRef<string[] | null>(null)
  const locale = useIntl().locale as Locale

  const { data, isSuccess, refetch } = useQuery(
    queries.translationList(),
    appResources.getTranslationList,
    { staleTime: Infinity }
  )
  const { mutate } = useMutation(appResources.postTranslation, {
    onSettled: refetch,
  })

  return useCallback(
    (id: string) => {
      const croppedId = cropString(id)

      if (!data) return croppedId

      const doesTranslationExist = locale && croppedId && data[locale]?.[croppedId]
      const hasTranslationBeenCreated = createdTranslationIds.current?.includes(croppedId)

      if (isSuccess && !doesTranslationExist && !hasTranslationBeenCreated) {
        createdTranslationIds.current = [...(createdTranslationIds.current || []), croppedId]
        mutate({
          locale,
          code: croppedId,
          text: id,
        })
      }

      return croppedId
    },
    [data, isSuccess, locale, mutate]
  )
}

// Returns translate function
export const useTranslation = () => {
  const { formatMessage } = useIntl()
  const prepareTranslation = usePrepareTranslation()

  return useCallback(
    (id: string, values: Record<string, string> = {}) =>
      formatMessage({ id: prepareTranslation(id) }, values),
    [formatMessage, prepareTranslation]
  )
}
